/**
 * License Plate Utility Functions - Verbeterde UX Versie
 *
 * Functies voor het opschonen, progressief detecteren, formatteren en valideren van Nederlandse kentekens.
 *
 * @since 2.0.0
 */

// Defineer alle sidecodes en hun volledige L/D patronen
const SIDECODE_PATTERNS = {
    1: 'LLDDDD', // AA-88-25
    2: 'DDDDLL', // 13-64-AD
    3: 'DDLLDD', // 74-VB-35
    4: 'LLDDLL', // PZ-99-ZH
    5: 'LLLLDD', // AB-CD-12
    6: 'DDLLLL', // 12-AB-CD
    7: 'DDLLLD', // 12-ABC-3 (7 tekens)
    8: 'DLLLDD', // 7-VB-01 (7 tekens)
    9: 'LLDDDL', // GB-001-B (7 tekens)
    10: 'LDDDLL', // G-001-BB (7 tekens)
    11: 'LLLDDL', // JLL-19-B (6 tekens)
};

// Defineer dash posities voor elke sidecode (telt van 1)
const SIDECODE_DASH_POSITIONS = {
    1: [ 2, 4 ], // LL-DD-DD
    2: [ 2, 4 ], // DD-DD-LL
    3: [ 2, 4 ], // DD-LL-DD
    4: [ 2, 4 ], // LL-DD-LL
    5: [ 2, 4 ], // LL-LL-DD
    6: [ 2, 4 ], // DD-LL-LL
    7: [ 2, 5 ], // DD-LLL-D (let op: na positie 5)
    8: [ 1, 4 ], // D-LLL-DD (let op: na positie 1 en 4)
    9: [ 2, 5 ], // LL-DDD-L (let op: na positie 5)
    10: [ 1, 4 ], // L-DDD-LL (let op: na positie 1 en 4)
    11: [ 3, 5 ], // LLL-DD-L (let op: na positie 3 en 5)
};

/**
 * Clean license plate input by removing separators and converting to uppercase.
 * (Ongewijzigd - deze functie is perfect.)
 *
 * @param {string} str - Raw license plate input
 * @return {string} Cleaned license plate (uppercase, alphanumeric only)
 */
export function cleanPlate( str ) {
    if ( ! str || typeof str !== 'string' ) {
        return '';
    }
    return str.toUpperCase().replace( /[^A-Z0-9]/g, '' );
}

/**
 * Converteert de cleaned string naar een patroon van L (Letter) en D (Cijfer).
 *
 * @param {string} cleaned - Cleaned license plate string
 * @return {string} Patroon string (bijv. 'LLDD')
 */
function createPattern( cleaned ) {
    return cleaned
        .split( '' )
        .map( ( char ) => ( /[A-Z]/.test( char ) ? 'L' : 'D' ) )
        .join( '' );
}

/**
 * Detecteert de sidecode op basis van het patroon, ook bij onvolledige invoer.
 *
 * De verbetering: De functie zoekt naar sidecodes waarvan het patroon begint met
 * het ingevoerde patroon. Als er meerdere matches zijn, wordt het resultaat 'null'
 * geretourneerd om onnodig streepjes-springen te voorkomen.
 *
 * @param {string} cleaned - Cleaned license plate string
 * @return {number|null} Sidecode (1-11) of null
 */
export function detectSidecodeProgressive( cleaned ) {
    if ( ! cleaned || cleaned.length === 0 ) {
        return null;
    }

    const currentPattern = createPattern( cleaned );
    const matchingSidecodes = [];

    for ( const [ sidecode, fullPattern ] of Object.entries( SIDECODE_PATTERNS ) ) {
        // Controleer of het huidige patroon overeenkomt met het begin van de volledige sidecode.
        if ( fullPattern.startsWith( currentPattern ) ) {
            matchingSidecodes.push( parseInt( sidecode, 10 ) );
        }
    }

    if ( matchingSidecodes.length === 1 ) {
        // Unieke match gevonden: perfect!
        return matchingSidecodes[ 0 ];
    }

    // Als er meer dan één match is, of geen match:
    // We keren null terug totdat de invoer uniek is, om te voorkomen dat we te vroeg formatteren.
    // Dit minimaliseert het ongewenste "streepje springen".
    return null;
}

/**
 * Formatteert het kenteken op basis van de herkende sidecode en de ingevoerde lengte.
 *
 * @param {string} cleaned - Cleaned license plate string
 * @param {number|null} sidecode - Detected sidecode (1-11)
 * @return {string} Formatted license plate (met streepjes)
 */
export function formatPlate( cleaned, sidecode ) {
    if ( ! cleaned || cleaned.length === 0 ) {
        return '';
    }

    // Valideer de sidecode en haal de posities op
    const dashPositions = SIDECODE_DASH_POSITIONS[ sidecode ];

    // Als de sidecode nog niet uniek is of onbekend is, toon dan de onopgemaakte invoer
    // Dit dwingt de gebruiker om door te typen totdat de sidecode uniek is.
    if ( ! sidecode || ! dashPositions ) {
        return cleaned;
    }

    let formatted = '';
    for ( let i = 0; i < cleaned.length; i++ ) {
        formatted += cleaned[ i ];
        // Voeg streepje toe als de huidige positie (i + 1) in de dashPositions array staat.
        if ( dashPositions.includes( i + 1 ) && i < cleaned.length - 1 ) {
            // Voeg alleen een streepje toe als er nog tekens volgen.
            formatted += '-';
        }
    }

    return formatted;
}

/**
 * Valideer of het kenteken volledig en correct is.
 *
 * @param {string} cleaned - Cleaned license plate string
 * @param {number|null} sidecode - Detected sidecode (1-11)
 * @return {boolean} True als de lengte en het patroon overeenkomen met de sidecode
 */
export function validatePattern( cleaned, sidecode ) {
    if ( ! cleaned || ! sidecode ) {
        return false;
    }

    // Zoek de verwachte lengte van de sidecode
    const expectedPattern = SIDECODE_PATTERNS[ sidecode ];
    if ( ! expectedPattern ) {
        return false;
    }

    // Controleer of de lengte overeenkomt EN of het volledige patroon overeenkomt
    if ( cleaned.length !== expectedPattern.length ) {
        return false;
    }

    // Controleer of de volledige invoer overeenkomt met het volledige patroon
    return createPattern( cleaned ) === expectedPattern;
}

/**
 * Process license plate input: clean, detect sidecode, and format.
 *
 * @param {string} input - Raw user input
 * @return {Object} Object met cleaned, sidecode, formatted, en isValid properties
 */
export function processPlate( input ) {
    const cleaned = cleanPlate( input );
    // Gebruik de progressieve detectie
    const sidecode = detectSidecodeProgressive( cleaned );
    const formatted = formatPlate( cleaned, sidecode );
    const isValid = validatePattern( cleaned, sidecode );

    return {
        cleaned,
        sidecode,
        formatted,
        isValid,
    };
}